/**
 * Schema-driven props editor for a selected page component (ADR-0010 P7c1 /
 * S15c).
 *
 * Built from the shared `inspectorKit` so it reads exactly like the Style panel:
 * tidy labelled rows (compact controls on the right, full-width editors stacked
 * under their label) with hairline separators. Controls are dispatched per
 * `props_schema` entry: `x-tray-expression` → `ExpressionField`; reference
 * pickers / enums → the kit's custom `Select` or `Segmented` (NEVER a native
 * `<select>`); boolean → `Switch`; number → a compact input; arrays / action
 * chains / column & row-action editors → their dedicated full-width editors.
 */
import { useState } from "react";

import { Switch } from "@/components/ui/switch";
import type { ResolvedPageInput } from "@/lib/pageInputs";

import { PageInputArgsEditor } from "./PageInputArgsEditor";
import { PAGE_SIZE_CHOICES } from "@/lib/dataGrid";
import type { ExprPreview } from "@/lib/pagePreview";
import type { PageNode, PagePropSchema, PageComponentSpec } from "@/lib/pageTypes";
import type { ResourceQuery } from "@/lib/resourceKinds";

import { ActionField } from "./ActionField";
import { BindableField } from "./BindableField";
import { DataSourceField } from "./DataSourceField";
import { CodeField } from "./CodeField";
import { ColumnsManagerField } from "./ColumnsManagerField";
import { ColumnsField } from "./ColumnsField";
import { Field, type KitOption, Segmented, Select } from "./inspectorKit";
import { RowActionsEditor } from "./RowActionsEditor";

export interface PropsInspectorProps {
  node: PageNode;
  spec: PageComponentSpec;
  /** Declared page-query names (for the `page_query_ref` picker). */
  queryNames: string[];
  /** Declared page queries WITH their `source_kind` — drives the segmented
   *  resource picker (Tabelle / Dataset / HTTP / JS) on the `data` field + the
   *  `run_query` action. Falls back to `queryNames` when absent. */
  queries?: ResourceQuery[];
  /** JS Object handler refs `<Name>.<method>` (ADR-0044 J2) for the `run_js`
   *  action picker. */
  jsFunctions?: string[];
  /** App forms (id + name) for the `app_form` picker. */
  forms: { id: string; name: string }[];
  /** App tables (id + name) for the `app_table` picker (record block). */
  tables: { id: string; name: string }[];
  /** Declared page-variable names (for the action-chain `set_variable` + the
   *  `page_var` picker on state_key/select_into). */
  varNames: string[];
  /** Register a new declared page variable (the `page_var` picker's "+ crea" —
   *  components reference variables, never create state anonymously). */
  onCreateVariable?: (name: string) => void;
  /** App pages (slug + title) for the row-action target-page picker (S5). Each
   *  page carries its RESOLVED `$input` contract (ADR-0045 R4-inputs) so the
   *  `page_ref` params editor renders a guided form for the picked target. */
  pages: { slug: string; title: string; inputs?: ResolvedPageInput[] }[];
  /** Overlay pages (slug + title + mode) for the `open_overlay` action picker
   *  (ADR-0023). */
  overlayPages?: { slug: string; title: string; mode: "modal" | "drawer" }[];
  /** In-page modal/drawer components (id + label) for the `open_modal` /
   *  `close_modal` action target picker (ADR-0045 R2). */
  modalTargets?: { id: string; label: string }[];
  /** Linked reports (id + label) for the `print_report` picker (ADR-0060 S5). */
  reportOptions?: { id: string; label: string }[];
  /** Column names available to the selected data_list (from its preview). */
  columnOptions: string[];
  /** Columns per table id — power the action-chain field-values picker (S13). */
  columnsByTable: Record<string, string[]>;
  /** Runnable workflows (id + name) for the run-workflow action picker (S14). */
  workflows: { id: string; name: string }[];
  /** App custom-component library (id + name) for the `custom_component`
   *  library picker (ADR-0022 #2). */
  customComponents: { id: string; name: string }[];
  /** Optional: pull a column's distinct values for a Multiselect filter (G1). */
  loadColumnValues?: (column: string) => Promise<string[]>;
  /** Ambient `.d.ts` for expression-field IntelliSense, derived from the page's
   *  live bindings ($user / $role / $queries / $page / $item) — see
   *  `lib/pagePreview.buildPageBindingsDts`. */
  exprAmbientDts?: string;
  /** Live-preview evaluator for `{{ }}` fields (renders `= value` under the
   *  editor) — `lib/pagePreview.previewTemplate` bound to the page scope. */
  previewExpr?: (template: string) => ExprPreview | null;
  /** Register the focused expression field's insert callback (Data panel → cursor). */
  registerInsert?: (insert: ((text: string) => void) | null) => void;
  onChange: (props: Record<string, unknown>) => void;
}

export function PropsInspector({
  node,
  spec,
  queryNames,
  queries,
  jsFunctions = [],
  forms,
  tables,
  varNames,
  onCreateVariable,
  pages,
  overlayPages = [],
  modalTargets = [],
  reportOptions = [],
  columnOptions,
  columnsByTable,
  workflows,
  customComponents,
  loadColumnValues,
  exprAmbientDts,
  previewExpr,
  registerInsert,
  onChange,
}: PropsInspectorProps) {
  const props = spec.props_schema.properties ?? {};
  const set = (key: string, value: unknown) => onChange({ ...node.props, [key]: value });

  // Conditional visibility (x-tray-show-when): a field renders only when every
  // named sibling prop equals the wanted value — keeps the panel uncluttered
  // (e.g. the column config hides until `filterable` OR `column_chooser` is on).
  // A plain object is an AND of equals; an ARRAY of objects is an OR of groups.
  const matchGroup = (group: Record<string, unknown>) =>
    Object.entries(group).every(([dep, want]) => {
      const v = node.props[dep];
      return Array.isArray(want) ? want.includes(v) : v === want;
    });
  const entries = Object.entries(props).filter(([, schema]) => {
    const showWhen = schema["x-tray-show-when"];
    if (!showWhen) return true;
    return Array.isArray(showWhen) ? showWhen.some(matchGroup) : matchGroup(showWhen);
  });
  if (entries.length === 0) {
    return <p className="text-sm text-muted-foreground">This component has no properties.</p>;
  }

  return (
    <div className="flex flex-col">
      {entries.map(([key, schema]) => (
        <PropField
          key={key}
          name={key}
          schema={schema}
          value={node.props[key]}
          nodeProps={node.props}
          queryNames={queryNames}
          queries={queries}
          jsFunctions={jsFunctions}
          forms={forms}
          tables={tables}
          varNames={varNames}
          onCreateVariable={onCreateVariable}
          pages={pages}
          overlayPages={overlayPages}
          modalTargets={modalTargets}
          reportOptions={reportOptions}
          columnOptions={columnOptions}
          columnsByTable={columnsByTable}
          workflows={workflows}
          customComponents={customComponents}
          loadColumnValues={loadColumnValues}
          exprAmbientDts={exprAmbientDts}
          previewExpr={previewExpr}
          registerInsert={registerInsert}
          onChange={(v) => set(key, v)}
        />
      ))}
    </div>
  );
}

const INPUT = "w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm";

function PropField({
  name,
  schema,
  value,
  nodeProps,
  queryNames,
  queries,
  jsFunctions,
  forms,
  tables,
  varNames,
  onCreateVariable,
  pages,
  overlayPages,
  modalTargets,
  reportOptions,
  columnOptions,
  columnsByTable,
  workflows,
  customComponents,
  loadColumnValues,
  exprAmbientDts,
  previewExpr,
  registerInsert,
  onChange,
}: {
  name: string;
  schema: PagePropSchema;
  value: unknown;
  nodeProps: Record<string, unknown>;
  queryNames: string[];
  queries?: ResourceQuery[];
  jsFunctions: string[];
  forms: { id: string; name: string }[];
  tables: { id: string; name: string }[];
  varNames: string[];
  onCreateVariable?: (name: string) => void;
  pages: { slug: string; title: string; inputs?: ResolvedPageInput[] }[];
  overlayPages: { slug: string; title: string; mode: "modal" | "drawer" }[];
  modalTargets: { id: string; label: string }[];
  reportOptions: { id: string; label: string }[];
  columnOptions: string[];
  columnsByTable: Record<string, string[]>;
  workflows: { id: string; name: string }[];
  customComponents: { id: string; name: string }[];
  loadColumnValues?: (column: string) => Promise<string[]>;
  exprAmbientDts?: string;
  previewExpr?: (template: string) => ExprPreview | null;
  registerInsert?: (insert: ((text: string) => void) | null) => void;
  onChange: (v: unknown) => void;
}) {
  const picker = schema["x-tray-picker"];

  // ---- full-width (stacked) editors ---------------------------------------
  // Raw code field (custom_component JSX) — a Monaco editor (JS/JSX + `model`
  // IntelliSense, expand-to-modal), NOT an expression toggle (the code is real
  // source, not a `{{ }}` template).
  if (schema["x-tray-code"]) {
    return (
      <CodeField
        label={name}
        hint={schema.description}
        value={typeof value === "string" ? value : ""}
        onChange={onChange}
      />
    );
  }
  if (schema["x-tray-column-filters"]) {
    return (
      <ColumnsManagerField
        value={value}
        columns={columnOptions}
        loadValues={loadColumnValues}
        // Show each per-column control only when its feature is on: filter mode
        // (G1) needs `filterable`, "visible by default" (G2) needs
        // `column_chooser`, "pin left" (G3) needs `pinnable`.
        showFilter={nodeProps.filterable === true}
        showVisibility={nodeProps.column_chooser === true}
        showPin={nodeProps.pinnable === true}
        onChange={onChange}
      />
    );
  }
  if (schema["x-tray-action-chain"]) {
    return (
      <ActionField
        label={name}
        value={value}
        varNames={varNames}
        overlayPages={overlayPages}
        modalTargets={modalTargets}
        reportOptions={reportOptions}
        tables={tables}
        columnsByTable={columnsByTable}
        workflows={workflows}
        queryNames={queryNames}
        queries={queries}
        jsFunctions={jsFunctions}
        ambientDts={exprAmbientDts}
        onChange={onChange}
      />
    );
  }
  if (schema["x-tray-row-actions"]) {
    return (
      <Field label={name} layout="stack">
        <RowActionsEditor value={value} pages={pages} onChange={onChange} />
      </Field>
    );
  }
  if (schema["x-tray-columns"]) {
    return (
      <Field label={name} layout="stack">
        <ColumnsField value={value} options={columnOptions} onChange={onChange} />
      </Field>
    );
  }
  if (schema["x-tray-width"]) {
    return (
      <Field label={name} layout="stack" hint={schema.description}>
        <WidthField value={String(value ?? "")} onChange={onChange} />
      </Field>
    );
  }
  if (schema["x-tray-page-size"]) {
    // Curated rows-per-page dropdown (NOT a free number input). Stores an int;
    // 0 = no pagination. The runtime then lets the user pick their own (G9).
    return (
      <Field label={name} hint={schema.description}>
        <Select
          value={String(Number(value ?? 0) || 0)}
          options={PAGE_SIZE_OPTIONS}
          onChange={(v) => onChange(Number(v))}
        />
      </Field>
    );
  }
  if (schema["x-tray-col-widths"]) {
    // Count-aware width presets (B). Stores an `fr` template string; "" = equal.
    // Options depend on the sibling `columns` count (a 2:1 split needs 2 cols).
    const count = Math.max(1, Math.min(6, Number(nodeProps.columns ?? 2)));
    const opts = columnWidthPresets(count);
    // A stored template whose token count no longer matches `columns` is shown
    // as "Equal" (the renderer ignores it too) so the control never looks stuck.
    const cur = opts.some((o) => o.value === value) ? String(value) : "";
    return (
      <Field label={name} hint={schema.description}>
        <Select value={cur} options={opts} onChange={onChange} />
      </Field>
    );
  }
  if (schema["x-tray-options-from-column"]) {
    // page_filter `options` (3b-2): a comma-separated list you can either type
    // or LOAD from the chosen column's distinct values (needs a `query_ref` +
    // `column` sibling so `loadColumnValues` knows the table).
    return (
      <OptionsFromColumnField
        label={name}
        value={String(value ?? "")}
        column={String(nodeProps.column ?? "")}
        loadValues={loadColumnValues}
        onChange={onChange}
      />
    );
  }
  // Data source (Appsmith): JS-off = a query DROPDOWN, JS-on = a `{{ }}` array
  // expression. Checked BEFORE the generic expression field (the `data` prop
  // carries both `x-tray-expression` and this picker).
  if (picker === "page_query_data") {
    return (
      <DataSourceField
        label={name}
        value={value}
        queryNames={queryNames}
        queries={queries}
        jsAmbientTypes={exprAmbientDts}
        previewExpr={previewExpr}
        registerInsert={registerInsert}
        onChange={onChange}
      />
    );
  }
  if (schema["x-tray-expression"]) {
    return (
      <BindableField
        label={name}
        hint={schema.description}
        value={value}
        layout="stack"
        fallback=""
        jsAmbientTypes={exprAmbientDts}
        previewExpr={previewExpr}
        registerInsert={registerInsert}
        onChange={onChange}
      >
        <input
          className={INPUT}
          value={typeof value === "string" ? value : ""}
          placeholder={schema.description}
          onChange={(e) => onChange(e.target.value)}
        />
      </BindableField>
    );
  }
  if (schema.type === "array") {
    return (
      <Field label={name} layout="stack" hint={schema.description}>
        <ColumnsField value={value} options={[]} onChange={onChange} />
      </Field>
    );
  }

  // ---- reference pickers (custom dropdown) --------------------------------
  if (picker === "page_query_column") {
    // A column dropdown driven by the sibling `query_ref`'s columns (3b-2). If
    // no query is picked yet there are no columns → fall back to free text so
    // the field is never a dead end.
    if (columnOptions.length > 0) {
      const opts: KitOption[] = [
        { value: "", label: "— select column —" },
        ...columnOptions.map((c) => ({ value: c, label: c })),
      ];
      return (
        <Field label={name} hint={schema.description}>
          <Select value={String(value ?? "")} options={opts} onChange={onChange} />
        </Field>
      );
    }
    return (
      <Field label={name} layout="stack" hint="Pick a query above to choose from its columns, or type a name.">
        <input
          className={INPUT}
          value={String(value ?? "")}
          placeholder="column name"
          onChange={(e) => onChange(e.target.value)}
        />
      </Field>
    );
  }
  if (picker === "page_var") {
    // A reference to a DECLARED page variable ($page). Components reference
    // variables, they don't create state anonymously — so this is a picker over
    // the registry + an inline "crea" that registers a new one (ADR: no
    // free-text state keys). A legacy/unknown current value is tolerated +
    // promotable so old pages don't break.
    return (
      <Field label={name} hint={schema.description}>
        <PageVarField
          value={typeof value === "string" ? value : ""}
          varNames={varNames}
          onChange={onChange}
          onCreate={onCreateVariable}
        />
      </Field>
    );
  }
  if (picker === "custom_component") {
    // Library-component reference (ADR-0022 #2): pick a reusable app component
    // by id; the node stores only the id and resolves its code live.
    const options: KitOption[] = [
      { value: "", label: "— select component —" },
      ...customComponents.map((c) => ({ value: c.id, label: c.name })),
    ];
    return (
      <Field
        label={name}
        hint={
          customComponents.length === 0
            ? "No library components yet — create one in the app's Components section."
            : schema.description
        }
      >
        <Select value={String(value ?? "")} options={options} onChange={onChange} />
      </Field>
    );
  }
  // ADR-0045 R4 — the `page_ref` component's page picker (which page to embed).
  if (picker === "page") {
    const options: KitOption[] = [
      { value: "", label: "— scegli una pagina —" },
      ...pages.map((p) => ({ value: p.slug, label: p.title })),
    ];
    return (
      <Field label={name}>
        <Select value={String(value ?? "")} options={options} onChange={onChange} />
      </Field>
    );
  }
  // ADR-0045 R4-params — the `page_ref` parameters: a name→`{{ expr }}` map
  // evaluated against THIS page and seeded as the embedded page's `$input`.
  if (picker === "page_params") {
    // Guided form from the TARGET page's resolved `$input` contract (declared
    // or auto-discovered): the operator no longer has to know what the embedded
    // page expects. The target slug is the sibling `page` prop.
    const targetSlug = String((nodeProps as Record<string, unknown>).page ?? "");
    const targetInputs = pages.find((p) => p.slug === targetSlug)?.inputs ?? [];
    return (
      <Field
        label={name}
        layout="stack"
        hint={
          targetSlug
            ? "Passati alla pagina come $input. Il valore può essere testo o JS ({{ }}) su questa pagina."
            : "Scegli prima una pagina sopra per vedere i suoi parametri."
        }
      >
        <PageInputArgsEditor
          value={value}
          onChange={onChange}
          inputs={targetInputs}
          jsAmbientTypes={exprAmbientDts}
          previewExpr={previewExpr}
          registerInsert={registerInsert}
        />
      </Field>
    );
  }
  if (picker === "page_query_ref" || picker === "app_form" || picker === "app_table") {
    const options: KitOption[] =
      picker === "page_query_ref"
        ? [{ value: "", label: "— none —" }, ...queryNames.map((q) => ({ value: q, label: q }))]
        : picker === "app_form"
          ? [{ value: "", label: "— none —" }, ...forms.map((f) => ({ value: f.id, label: f.name }))]
          : [{ value: "", label: "— none —" }, ...tables.map((t) => ({ value: t.id, label: t.name }))];
    return (
      <Field label={name}>
        <Select value={String(value ?? "")} options={options} onChange={onChange} />
      </Field>
    );
  }

  // ---- enums: a button group for few options, a dropdown for many ---------
  // Every scalar field carries the JS toggle (ADR-0044 U4): JS off = the tidy
  // structured control below, JS on = a `{{ }}` binding.
  const exprProps = {
    jsAmbientTypes: exprAmbientDts,
    previewExpr,
    registerInsert,
    onChange,
  } as const;

  if (schema.enum) {
    const options: KitOption[] = schema.enum.map((o) => ({ value: o, label: o }));
    const fallback = String(schema.default ?? schema.enum[0]);
    const current = String(value ?? fallback);
    return (
      <BindableField label={name} value={value} layout="row" fallback={fallback} {...exprProps}>
        {schema.enum.length <= 4 ? (
          <Segmented value={current} options={options} onChange={(v) => onChange(v)} />
        ) : (
          <Select value={current} options={options} onChange={onChange} />
        )}
      </BindableField>
    );
  }

  // ---- boolean ------------------------------------------------------------
  if (schema.type === "boolean") {
    return (
      <BindableField
        label={name}
        hint={schema.description}
        value={value}
        layout="row"
        fallback={Boolean(schema.default)}
        {...exprProps}
      >
        <Switch checked={Boolean(value ?? schema.default)} onCheckedChange={(v) => onChange(v)} />
      </BindableField>
    );
  }

  // ---- number -------------------------------------------------------------
  if (schema.type === "integer" || schema.type === "number") {
    return (
      <BindableField label={name} value={value} layout="row" fallback={undefined} {...exprProps}>
        <input
          type="number"
          className="w-24 rounded-md border border-border bg-background px-2 py-1.5 text-right text-sm"
          value={value == null ? "" : Number(value)}
          min={schema.minimum}
          max={schema.maximum}
          onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))}
        />
      </BindableField>
    );
  }

  // ---- plain string (full-width) ------------------------------------------
  return (
    <BindableField label={name} hint={schema.description} value={value} layout="stack" fallback="" {...exprProps}>
      <input
        className={INPUT}
        value={typeof value === "string" ? value : ""}
        placeholder={schema.description}
        onChange={(e) => onChange(e.target.value)}
      />
    </BindableField>
  );
}

/** page_filter `options` editor (3b-2): a comma-separated text you can type OR
 *  populate from the chosen column's distinct values ("Load values from data").
 *  The button is enabled only once a `query_ref` + `column` are set (so
 *  `loadValues` can resolve the table) — otherwise typing is the path. */
function OptionsFromColumnField({
  label,
  value,
  column,
  loadValues,
  onChange,
}: {
  label: string;
  value: string;
  column: string;
  loadValues?: (column: string) => Promise<string[]>;
  onChange: (v: string) => void;
}) {
  const [loading, setLoading] = useState(false);
  const canLoad = Boolean(loadValues) && column.trim() !== "";
  const load = async () => {
    if (!loadValues || !column) return;
    setLoading(true);
    try {
      onChange((await loadValues(column)).join(", "));
    } catch {
      /* leave the typed value untouched on failure */
    } finally {
      setLoading(false);
    }
  };
  return (
    <Field label={label} layout="stack" hint="Comma-separated. Type them, or load the column's distinct values.">
      <input
        className={INPUT}
        value={value}
        placeholder="won, lost, …"
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        disabled={!canLoad || loading}
        onClick={load}
        className="mt-1 self-start rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-foreground/5 disabled:opacity-40"
      >
        {loading ? "Loading…" : "Load values from data"}
      </button>
    </Field>
  );
}

/** Rows-per-page choices for the data_list `page_size` (curated dropdown, not a
 *  free number input). 0 = no pagination; the runtime user then picks their own
 *  rows-per-page (G9) from the SAME curated set (`PAGE_SIZE_CHOICES`) so the two
 *  never diverge. Kit `Select` options (string values, coerced to int). */
const PAGE_SIZE_OPTIONS: KitOption[] = [
  { value: "0", label: "All (no pagination)" },
  ...PAGE_SIZE_CHOICES.map((n) => ({ value: String(n), label: `${n} / page` })),
];

/** Count-aware grid column-width presets (B). Each value is a CSS `fr` template
 *  with exactly `count` tokens (so the renderer applies it); "" = equal columns.
 *  Only the common ratios are offered — fancy templates aren't worth the UX. */
function columnWidthPresets(count: number): KitOption[] {
  const equal: KitOption[] = [{ value: "", label: `Equal (${count})` }];
  if (count === 2)
    return [
      ...equal,
      { value: "2fr 1fr", label: "Wide left · 2 : 1" },
      { value: "1fr 2fr", label: "Wide right · 1 : 2" },
      { value: "3fr 2fr", label: "60 / 40" },
      { value: "2fr 3fr", label: "40 / 60" },
      { value: "3fr 1fr", label: "Sidebar left · 3 : 1" },
      { value: "1fr 3fr", label: "Sidebar right · 1 : 3" },
    ];
  if (count === 3)
    return [
      ...equal,
      { value: "1fr 2fr 1fr", label: "Wide center" },
      { value: "2fr 1fr 1fr", label: "Wide left" },
      { value: "1fr 1fr 2fr", label: "Wide right" },
    ];
  if (count === 4) return [...equal, { value: "2fr 1fr 1fr 1fr", label: "Wide first" }];
  return equal;
}

/** Width preset picker for the page `max_width` (S8b): Inherit / Narrow / Wide
 *  / Full / Custom. Uses the kit `Select` (no native dropdown). */
const WIDTH_PRESETS: { label: string; value: string }[] = [
  { label: "Inherit (app default)", value: "" },
  { label: "Narrow (768)", value: "768px" },
  { label: "Wide (1280)", value: "1280px" },
  { label: "Full width", value: "full" },
];

function WidthField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const isPreset = WIDTH_PRESETS.some((p) => p.value === value);
  const options: KitOption[] = [
    ...WIDTH_PRESETS.map((p) => ({ value: p.value || "__inherit", label: p.label })),
    { value: "__custom", label: "Custom…" },
  ];
  const current = isPreset ? value || "__inherit" : "__custom";
  return (
    <div className="flex flex-col gap-1.5">
      <Select
        value={current}
        options={options}
        align="left"
        onChange={(v) => {
          if (v === "__custom") onChange(value || "1024px");
          else onChange(v === "__inherit" ? "" : v);
        }}
      />
      {!isPreset && (
        <input
          className={INPUT}
          value={value}
          placeholder="e.g. 960px, 80%"
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

/** Page-variable reference field (`x-tray-picker: "page_var"`): a picker over
 *  DECLARED variables + an inline "crea" that registers a new one, so a
 *  component never invents anonymous state. A legacy free-text value (not in the
 *  registry) is shown + promotable so old pages keep working. */
function PageVarField({
  value,
  varNames,
  onChange,
  onCreate,
}: {
  value: string;
  varNames: string[];
  onChange: (v: string) => void;
  onCreate?: (name: string) => void;
}) {
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState("");

  if (creating) {
    const commit = () => {
      const n = draft.trim();
      if (n) {
        onCreate?.(n);
        onChange(n);
      }
      setCreating(false);
      setDraft("");
    };
    return (
      <div className="flex items-center gap-1">
        <input
          autoFocus
          aria-label="Nome nuova variabile"
          className={INPUT}
          value={draft}
          placeholder="nome variabile"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") setCreating(false);
          }}
        />
        <button type="button" className="shrink-0 px-1 text-accent" onClick={commit}>crea</button>
        <button type="button" className="shrink-0 px-1 text-muted-foreground" onClick={() => setCreating(false)}>annulla</button>
      </div>
    );
  }

  const unregistered = Boolean(value) && !varNames.includes(value);
  const CREATE = " create"; // sentinel that can't collide with a var name
  const options: KitOption[] = [
    { value: "", label: "— nessuna —" },
    ...varNames.map((n) => ({ value: n, label: n })),
    ...(unregistered ? [{ value, label: `${value} (non registrata)` }] : []),
    ...(onCreate ? [{ value: CREATE, label: "+ Crea variabile…" }] : []),
  ];
  return (
    <div className="flex items-center gap-1">
      <Select
        value={value}
        options={options}
        onChange={(v) => {
          if (v === CREATE) {
            setDraft("");
            setCreating(true);
          } else {
            onChange(v);
          }
        }}
      />
      {unregistered && onCreate && (
        <button
          type="button"
          className="shrink-0 rounded border border-border px-1.5 py-0.5 text-[10px] text-accent hover:bg-surface"
          title="Registra questa chiave come variabile"
          onClick={() => onCreate(value)}
        >
          registra
        </button>
      )}
    </div>
  );
}

